import { createConsumer, type Consumer } from "@rails/actioncable"

import { clientApiFetch } from "@/lib/api/client/fetch"
import {
  CABLE_RECONNECT_BASE_MS,
  CABLE_RECONNECT_JITTER,
  CABLE_RECONNECT_MAX_MS,
} from "@/lib/notifications/config"

// The page's single ActionCable connection, shared by every channel (chat,
// notifications). It authenticates with a single-use, 30-second ticket
// (§12.5), never the session token: the URL of a WebSocket ends up in load
// balancer logs, history and Referer.
//
// ActionCable reads the URL on every connection attempt, so each attempt
// gets its own ticket. A new one is requested as soon as the connection
// drops, which is before ActionCable's monitor retries. When the server
// refuses a connection (a used or expired ticket) ActionCable gives up for
// good, so this module reconnects itself, with capped, jittered backoff.

const CABLE_URL = process.env.NEXT_PUBLIC_CABLE_URL ?? "ws://localhost:3000/cable"

let consumer: Consumer | null = null
let consumerRequest: Promise<Consumer> | null = null
let nextTicket: string | null = null
let ticketRequest: Promise<void> | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectAttempts = 0
// Bumped by resetCableConsumer: a consumer still being created for the
// previous session is disconnected instead of handed out.
let generation = 0

async function requestTicket(): Promise<string> {
  const { ticket } = await clientApiFetch<{ ticket: string }>("/api/auth/cable-ticket", {
    method: "POST",
    cache: "no-store",
  })
  return ticket
}

function refreshTicket(): Promise<void> {
  ticketRequest ??= requestTicket()
    .then((ticket) => {
      nextTicket = ticket
    })
    .finally(() => {
      ticketRequest = null
    })
  return ticketRequest
}

// Without a ticket the server refuses the attempt, which lands in the
// refused-connection path below and retries with a fresh one.
function cableUrl(): string {
  const ticket = nextTicket
  nextTicket = null
  return `${CABLE_URL}?ticket=${ticket ? encodeURIComponent(ticket) : ""}`
}

export async function getCableConsumer(): Promise<Consumer> {
  if (consumer) return consumer
  const requestedFor = generation
  const request = (consumerRequest ??= refreshTicket()
    .then(() => {
      const created = createConsumer(cableUrl)
      if (requestedFor !== generation) {
        created.disconnect()
        return created
      }
      consumer = created
      return created
    })
    .finally(() => {
      if (consumerRequest === request) consumerRequest = null
    }))
  return request
}

// The socket is authenticated for the session it opened with: a logout or a
// login in the same page must drop it, or the next user would share the
// previous one's stream. Channel hooks resubscribe on their next mount.
export function resetCableConsumer(): void {
  generation += 1
  consumer?.disconnect()
  consumer = null
  consumerRequest = null
  nextTicket = null
  if (reconnectTimer) clearTimeout(reconnectTimer)
  reconnectTimer = null
  reconnectAttempts = 0
}

function backoffDelay(attempt: number): number {
  const exponential = Math.min(CABLE_RECONNECT_MAX_MS, CABLE_RECONNECT_BASE_MS * 2 ** attempt)
  const jitter = 1 + CABLE_RECONNECT_JITTER * (2 * Math.random() - 1)
  return Math.min(CABLE_RECONNECT_MAX_MS, Math.round(exponential * jitter))
}

function scheduleReconnect(): void {
  if (reconnectTimer) return
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    refreshTicket()
      .then(() => {
        consumer?.connect()
      })
      .catch(() => {
        scheduleReconnect()
      })
  }, backoffDelay(reconnectAttempts++))
}

// Channel subscriptions report their connection state here; several may
// report the same drop, which requests one ticket and one reconnect.
export function reportCableDisconnected(willAttemptReconnect: boolean): void {
  if (willAttemptReconnect) {
    refreshTicket().catch(() => {
      // The next attempt goes without a ticket, is refused, and retries.
    })
    return
  }
  scheduleReconnect()
}

export function reportCableConnected(): void {
  reconnectAttempts = 0
}
